import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faSpinner, faEdit, faTrash, faSave, faXmark, faDownload, faFilePdf, faFileExcel,faAngleRight } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { BASE_URL } from '../../config';

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

const CardPrintingFeeDetails = () => {
  const { seriesNoUid } = useParams();
  const query = useQuery();
  const series_no = query.get('series');
  const navigate = useNavigate();

  // State for table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  // State for editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    net_amount: '',
    vat_amount: '',
    gross_amount: '',
    member_type: 'P',
  });

  // Formatter for currency
  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Fetch series details
  const fetchSeriesDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/series/${seriesNoUid}`, {
        params: { page: currentPage, per_page: itemsPerPage },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setRecords(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Failed to fetch series details.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle file download (ZIP with PDFs and XLSX)
  const handleDownloadFiles = async () => {
    try {
      setFileLoading(true);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/series/${seriesNoUid}/pdfs`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CardPrintingFee_Series_${series_no}_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Files downloaded successfully.', { duration: 3000 });
    } catch (error) {
      toast.error('Failed to download files.', { duration: 3000 });
    } finally {
      setFileLoading(false);
    }
  };

  // Handle edit
  const handleEditClick = (record) => {
    setEditingId(record.id);
    setEditForm({
      net_amount: record.net_amount,
      vat_amount: record.vat_amount,
      gross_amount: record.gross_amount,
      member_type: record.member_type,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ net_amount: '', vat_amount: '', gross_amount: '', member_type: 'P' });
  };

  const handleEditFormChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editForm.net_amount || !editForm.vat_amount || !editForm.gross_amount) {
      toast.error('Please fill in all fields.', { duration: 3000 });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${BASE_URL}/card-printing-fee/record/${editingId}`,
        {
          net_amount: parseFloat(editForm.net_amount),
          vat_amount: parseFloat(editForm.vat_amount),
          gross_amount: parseFloat(editForm.gross_amount),
          member_type: editForm.member_type,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      toast.success(response.data.message, { duration: 3000 });
      setEditingId(null);
      setEditForm({ net_amount: '', vat_amount: '', gross_amount: '', member_type: 'P' });
      fetchSeriesDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update record.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      setLoading(true);
      const response = await axios.delete(`${BASE_URL}/card-printing-fee/record/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      toast.success(response.data.message, { duration: 3000 });
      fetchSeriesDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete record.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data
  useEffect(() => {
    fetchSeriesDetails();
  }, [seriesNoUid, currentPage, itemsPerPage]);

  // Table columns
  const columns = [
    { key: 'series_no', label: 'Series No.' },
    { key: 'member_id', label: 'Member ID' },
    { key: 'employee_id', label: 'Employee ID' },
    { key: 'member_name', label: 'Name' },
    { key: 'corp_code', label: 'Corp Code' },
    { key: 'billing_type', label: 'Billing Type' },
    { key: 'date_enrolled', label: 'Enrolled' },
    { key: 'net_amount', label: 'Net' },
    { key: 'vat_amount', label: 'VAT' },
    { key: 'gross_amount', label: 'Gross' },
    { key: 'member_type', label: 'Type' },
    { key: 'actions', label: 'Actions' },
  ];

  // Pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} records`;
  };

  // Loading Skeleton
  const LoadingSkeleton = ({ rows = 5, columns = 12 }) => (
    <>
      {Array(rows).fill().map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-gray-100">
          {Array(columns).fill().map((_, j) => (
            <td key={j} className="p-2">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/billing/card-printing-fee-billed')}
            className="p-2 bg-indigo-600 text-white rounded-lg"
          >
            <FontAwesomeIcon icon={faAngleLeft} className="text-sm" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">Series {series_no} Details</h1>
            <p className="text-xs text-gray-500">Manage records for series {series_no}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadFiles}
              className="px-3 py-1.5 text-xs text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-1 disabled:bg-red-400"
              disabled={fileLoading}
            >
              <FontAwesomeIcon icon={fileLoading ? faSpinner : faFilePdf} spin={fileLoading} />
              PDF
            </button>
            <button
              onClick={handleDownloadFiles}
              className="px-3 py-1.5 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1 disabled:bg-green-400"
              disabled={fileLoading}
            >
              <FontAwesomeIcon icon={fileLoading ? faSpinner : faFileExcel} spin={fileLoading} />
              XLSX
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Table */}
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key} className="p-2 text-left font-semibold text-gray-600">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-2 text-center text-gray-500">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50 transition">
                        <td className="p-2">{record.series_no}</td>
                        <td className="p-2">{record.member_id}</td>
                        <td className="p-2">{record.employee_id}</td>
                        <td className="p-2">{record.member_name}</td>
                        <td className="p-2">{record.corp_code}</td>
                        <td className="p-2">{record.billing_type}</td>
                        <td className="p-2">{record.date_enrolled}</td>
                        <td className="p-2">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.net_amount}
                              onChange={(e) => handleEditFormChange('net_amount', e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.net_amount)
                          )}
                        </td>
                        <td className="p-2">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.vat_amount}
                              onChange={(e) => handleEditFormChange('vat_amount', e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.vat_amount)
                          )}
                        </td>
                        <td className="p-2">
                          {editingId === record.id ? (
                            <input
                              type="number"
                              value={editForm.gross_amount}
                              onChange={(e) => handleEditFormChange('gross_amount', e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            phpFormatter.format(record.gross_amount)
                          )}
                        </td>
                        <td className="p-2">
                          {editingId === record.id ? (
                            <select
                              value={editForm.member_type}
                              onChange={(e) => handleEditFormChange('member_type', e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500"
                            >
                              <option value="P">Principal</option>
                              <option value="D">Dependent</option>
                            </select>
                          ) : (
                            record.member_type.toLowerCase() === 'p' ? 'Principal' : 'Dependent'
                          )}
                        </td>
                        <td className="p-2 flex gap-2">
                          {editingId === record.id ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 hover:text-green-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              {/* <button
                                onClick={() => handleEditClick(record)}
                                className="text-blue-600 hover:text-blue-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(record.id)}
                                className="text-red-600 hover:text-red-800"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                              </button> */}
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>{val}/page</option>
                ))}
              </select>
              <span>{getShowingEntriesText()}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-xs text-gray-600">{currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {fileLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="text-indigo-600 text-xl animate-spin" />
              <p className="text-xs text-gray-800">Generating files...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPrintingFeeDetails;